/**
 * Maps price-list compare API results into table rows shaped like
 * „Listă în lucru” (one row per product / modified-or-new furnizor).
 */

import { calculatePricingValueChangeRatio } from '@/utils/priceListPricingFieldBaselines';
import {
    calculatePriceAdjustmentRatio,
    parseNumericInput,
} from '@/utils/priceListProductCalculator';
import {
    findMatchingLiveFurnizorTab,
    isFurnizorTabModified,
    resolveFurnizorPricingLabel,
    resolveProductNotes,
} from '@/utils/priceListTablePricing';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * @param {Object|null|undefined} payload
 * @returns {Object[]}
 */
export function getComparePayloadFurnizorTabs(payload) {
    const tabs = payload?.pricing_detail?.furnizorPricings;
    return Array.isArray(tabs) ? tabs : [];
}

/**
 * @param {Object|null|undefined} payload
 * @param {Object|null|undefined} tab
 * @returns {number|null}
 */
function pickSalePrice(payload, tab) {
    return toNullableNumber(
        tab?.listPriceRonIncVatFinal
            ?? tab?.manualListPriceRonIncVatFinal
            ?? tab?.overrideListPriceRonIncVatFinalValue
            ?? payload?.item?.price
            ?? payload?.list_price,
    );
}

/**
 * @param {Object|null|undefined} payload
 * @param {Object|null|undefined} tab
 * @returns {number|null}
 */
function pickEntryPrice(payload, tab) {
    return toNullableNumber(
        tab?.entryPriceRonExVat
            ?? payload?.pricing_detail?.entryPriceRonExVat,
    );
}

/**
 * @param {Object|null|undefined} tab
 * @param {Object|null|undefined} detail
 * @returns {number|null}
 */
function pickSupplierListPrice(tab, detail) {
    return toNullableNumber(
        tab?.supplierListPriceNew
            ?? tab?.supplierListPriceOld
            ?? detail?.supplierListPriceNew
            ?? detail?.supplierListPriceOld,
    );
}

/**
 * @param {Object|null|undefined} tab
 * @param {Object|null|undefined} detail
 * @returns {number|null}
 */
function pickMarkup(tab, detail, key) {
    return toNullableNumber(tab?.[key] ?? detail?.[key]);
}

/**
 * @param {Object|null|undefined} leftPayload
 * @param {Object|null|undefined} rightPayload
 * @returns {{ leftTab: Object|null, rightTab: Object|null, kind: string }[]}
 */
export function selectCompareFurnizorTabPairs(leftPayload, rightPayload) {
    const leftTabs = getComparePayloadFurnizorTabs(leftPayload);
    const rightTabs = getComparePayloadFurnizorTabs(rightPayload);

    if (!leftTabs.length && !rightTabs.length) {
        return [{ leftTab: null, rightTab: null, kind: 'update' }];
    }

    /** @type {{ leftTab: Object|null, rightTab: Object|null, kind: string }[]} */
    const pairs = [];

    for (const rightTab of rightTabs) {
        const leftTab = findMatchingLiveFurnizorTab(rightTab, leftTabs);
        if (!leftTab) {
            pairs.push({ leftTab: null, rightTab, kind: 'new_furnizor' });
            continue;
        }
        if (isFurnizorTabModified(rightTab, leftTab)) {
            pairs.push({ leftTab, rightTab, kind: 'update' });
        }
    }

    for (const leftTab of leftTabs) {
        if (!findMatchingLiveFurnizorTab(leftTab, rightTabs)) {
            pairs.push({ leftTab, rightTab: null, kind: 'removed_furnizor' });
        }
    }

    if (!pairs.length) {
        const rightDefault = rightTabs.find((tab) => tab?.isDefault) || rightTabs[0] || null;
        const leftDefault = rightDefault
            ? findMatchingLiveFurnizorTab(rightDefault, leftTabs)
            : (leftTabs.find((tab) => tab?.isDefault) || leftTabs[0] || null);
        pairs.push({
            leftTab: leftDefault,
            rightTab: rightDefault || leftDefault,
            kind: 'update',
        });
    }

    return pairs;
}

/**
 * Stable identity for a furnizor tab used in compare row keys.
 * Mirrors findMatchingLiveFurnizorTab fallbacks so multi-furnizor products
 * without clientKey still get distinct Vue / VDataTable keys.
 *
 * @param {Object|null|undefined} tab
 * @returns {string}
 */
function resolveCompareTabKey(tab) {
    if (!tab || typeof tab !== 'object') {
        return '';
    }
    return String(
        tab.clientKey
        || tab.furnizorCode
        || tab.furnizorId
        || tab.id
        || tab.furnizorName
        || '',
    ).trim();
}

/**
 * @param {Object} params
 * @returns {Object}
 */
function buildCompareDisplayRow({
    status,
    kind,
    leftPayload = null,
    rightPayload = null,
    leftTab = null,
    rightTab = null,
    changes = [],
    rowIndex = 0,
}) {
    const primaryPayload = rightPayload || leftPayload || {};
    const secondaryPayload = leftPayload || rightPayload || {};
    const item = primaryPayload.item || {};
    const detail = primaryPayload.pricing_detail || null;
    const tab = rightTab || leftTab;
    const manufacturerName = String(
        detail?.manufacturerName
            || secondaryPayload?.pricing_detail?.manufacturerName
            || '',
    ).trim();

    const productCode = String(
        item.product_code
            || secondaryPayload?.item?.product_code
            || '',
    ).trim();
    const productName = String(
        item.product_name
            || secondaryPayload?.item?.product_name
            || '',
    ).trim();

    const isNewFurnizor = kind === 'new_furnizor';
    const isRemovedFurnizor = kind === 'removed_furnizor';

    const supplierOld = (status === 'added' || isNewFurnizor)
        ? null
        : pickSupplierListPrice(leftTab, leftPayload?.pricing_detail);
    const supplierNew = (status === 'removed' || isRemovedFurnizor)
        ? null
        : pickSupplierListPrice(rightTab || tab, detail);

    const entryOld = (status === 'added' || isNewFurnizor)
        ? null
        : pickEntryPrice(leftPayload, leftTab);
    const entryNew = (status === 'removed' || isRemovedFurnizor)
        ? null
        : pickEntryPrice(rightPayload || primaryPayload, rightTab || tab);

    const previousPrice = (status === 'added' || isNewFurnizor)
        ? null
        : pickSalePrice(leftPayload, leftTab);
    const price = (status === 'removed' || isRemovedFurnizor)
        ? null
        : pickSalePrice(rightPayload || primaryPayload, rightTab || tab);

    const currencyRaw = String(
        tab?.currency
            || detail?.currency
            || leftTab?.currency
            || leftPayload?.pricing_detail?.currency
            || 'ron',
    ).trim().toLowerCase() || 'ron';

    const tabKey = resolveCompareTabKey(tab);
    // Always include kind + rowIndex so multi-furnizor / product-level rows
    // never collide (VDataTable item-value="id" requires uniqueness).
    const rowId = [
        status,
        productCode || 'unknown',
        kind || 'update',
        tabKey || 'default',
        String(rowIndex),
    ].join('::');

    return {
        id: rowId,
        status,
        draft_kind: kind,
        product_code: productCode,
        product_name: productName,
        manufacturer_product_code: String(
            tab?.manufacturerProductCode
                ?? detail?.manufacturerProductCode
                ?? '',
        ).trim(),
        manufacturer_product_name: String(
            tab?.manufacturerProductName
                ?? detail?.manufacturerProductName
                ?? '',
        ).trim(),
        measurement_unit: String(
            item.measurement_unit
                || secondaryPayload?.item?.measurement_unit
                || '',
        ).trim(),
        serial_number: Boolean(detail?.serialNumber),
        furnizor: resolveFurnizorPricingLabel(tab, manufacturerName),
        furnizorTabKey: tabKey || null,
        categories: [],
        category_assignments:
            primaryPayload.category_assignments
            || secondaryPayload.category_assignments
            || [],
        systems: detail?.systems || [],
        system1: detail?.system1,
        system2: detail?.system2,
        system3: detail?.system3,
        notes: resolveProductNotes({ pricing_detail: detail })
            || resolveProductNotes({ pricing_detail: secondaryPayload?.pricing_detail }),
        supplier_list_price_old: supplierOld,
        supplier_list_price_new: supplierNew,
        supplier_list_price_adjustment_ratio: calculatePricingValueChangeRatio(
            supplierNew,
            supplierOld,
        ),
        currency: currencyRaw,
        is_default: tab ? Boolean(tab.isDefault) : true,
        entry_price_ron_inc_vat_old: entryOld,
        entry_price_ron_inc_vat: entryNew,
        entry_price_adjustment_ratio: calculatePricingValueChangeRatio(
            entryNew,
            entryOld,
        ),
        markup_reference: pickMarkup(tab, detail, 'markupReference'),
        markup_actual: pickMarkup(tab, detail, 'markupActual'),
        previous_price: previousPrice,
        price,
        price_adjustment_ratio:
            previousPrice != null && price != null
                ? calculatePriceAdjustmentRatio(previousPrice, price)
                : null,
        changes: Array.isArray(changes) ? changes : [],
        left_payload: leftPayload,
        right_payload: rightPayload,
        payload: primaryPayload,
        leftTab,
        rightTab,
    };
}

/**
 * Builds display rows for the compare table (lista-în-lucru shape).
 *
 * @param {Object|null|undefined} result - API compare response
 * @returns {Object[]}
 */
export function buildCompareDisplayRows(result) {
    if (!result) {
        return [];
    }

    /** @type {Object[]} */
    const rows = [];
    let rowIndex = 0;

    for (const entry of result.added || []) {
        const payload = entry.payload || null;
        const tabs = getComparePayloadFurnizorTabs(payload);
        if (!tabs.length) {
            rows.push(buildCompareDisplayRow({
                status: 'added',
                kind: 'new',
                rightPayload: payload,
                rowIndex: rowIndex++,
            }));
            continue;
        }
        for (const tab of tabs) {
            rows.push(buildCompareDisplayRow({
                status: 'added',
                kind: 'new',
                rightPayload: payload,
                rightTab: tab,
                rowIndex: rowIndex++,
            }));
        }
    }

    for (const entry of result.removed || []) {
        const payload = entry.payload || null;
        const tabs = getComparePayloadFurnizorTabs(payload);
        if (!tabs.length) {
            rows.push(buildCompareDisplayRow({
                status: 'removed',
                kind: 'removed',
                leftPayload: payload,
                rowIndex: rowIndex++,
            }));
            continue;
        }
        for (const tab of tabs) {
            rows.push(buildCompareDisplayRow({
                status: 'removed',
                kind: 'removed',
                leftPayload: payload,
                leftTab: tab,
                rowIndex: rowIndex++,
            }));
        }
    }

    for (const entry of result.changed || []) {
        const leftPayload = entry.left_payload || null;
        const rightPayload = entry.right_payload || null;
        const changes = entry.changes || [];
        const pairs = selectCompareFurnizorTabPairs(leftPayload, rightPayload);
        for (const pair of pairs) {
            rows.push(buildCompareDisplayRow({
                status: 'changed',
                kind: pair.kind === 'removed_furnizor' ? 'removed_furnizor' : pair.kind,
                leftPayload,
                rightPayload,
                leftTab: pair.leftTab,
                rightTab: pair.rightTab,
                changes,
                rowIndex: rowIndex++,
            }));
        }
    }

    return rows;
}

/**
 * Converts a compare snapshot payload into an editItem suitable for the
 * product builder (read-only view).
 *
 * @param {Object|null|undefined} payload
 * @returns {Object|null}
 */
export function comparePayloadToEditItem(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const item = payload.item || {};
    const productCode = String(item.product_code || '').trim();
    if (!productCode) {
        return null;
    }
    const assignments = Array.isArray(payload.category_assignments)
        ? payload.category_assignments
        : [];
    const categories = assignments
        .map((row) => row?.main_category)
        .filter(Boolean);

    return {
        product_code: productCode,
        product_name: item.product_name || '',
        measurement_unit: item.measurement_unit || '',
        price: item.price ?? payload.list_price ?? null,
        description: item.description || '',
        visible_in_offer_app: Boolean(item.visibleInOfferApp),
        pricing_detail: payload.pricing_detail || null,
        has_pricing_detail: Boolean(payload.pricing_detail),
        category_assignments: assignments,
        categories,
        serial_number: Boolean(payload.pricing_detail?.serialNumber),
    };
}
